/**
 * Proof screenshots a mission wrote to .orgus-dev/shots/ in its worktree, kept on the mission
 * when Orgus removed the worktree (QA-75): a compact grid of thumbnails, each opening full size.
 */
import { useMemo, useState } from 'react';
import { ImageOff, Images } from '@orgus/ui/icons';
import type { Attachment, AttachmentTarget } from '@orgus/protocol';
import { EmptyState, ErrorState, Skeleton } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { toView, useAttachmentsOn, useAttachmentsState } from '../attachments/data.ts';

/** The hub names the shots it keeps with this prefix (apps/hub/src/ext/missions/cleanup.ts). */
export const PROOF_PREFIX = 'proof-';

/** A mission's proof images, oldest first. */
export function proofShots(files: readonly Attachment[]): Attachment[] {
  return files.filter((a) => a.kind === 'image' && a.name.startsWith(PROOF_PREFIX)).sort((a, b) => a.createdAt - b.createdAt);
}

export function ProofGallery({ target }: { target: AttachmentTarget }) {
  const phase = useOrgus((s) => s.phase);
  const state = useAttachmentsState();
  const files = useAttachmentsOn(target);
  const views = useMemo(() => proofShots(files).map(toView), [files]);
  const [broken, setBroken] = useState<ReadonlySet<string>>(new Set());

  if (phase === 'loading') return <Skeleton className="block h-20 w-full" />;
  if (!state) {
    return <ErrorState variant="inline" error={{ what: 'Screenshots are not available.', why: 'This hub has no attachments module.', next: 'Update Orgus on the hub machine.' }} />;
  }
  if (!views.length) {
    return <EmptyState icon={<Images className="size-5" />} title="No screenshots yet" description="Images the mission saves to .orgus-dev/shots/ appear here once it lands." />;
  }
  return (
    <ul className="grid grid-cols-4 gap-2" aria-label="Screenshots">
      {views.map((v) => (
        <li key={v.id}>
          <a
            href={v.url}
            target="_blank"
            rel="noreferrer"
            title={v.name.slice(PROOF_PREFIX.length)}
            className="flex aspect-video items-center justify-center overflow-hidden rounded-control border border-border-subtle bg-sunken focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            {broken.has(v.id) || !v.thumbUrl ? (
              <ImageOff className="size-4 text-fg-subtle" aria-label={`${v.name.slice(PROOF_PREFIX.length)} could not be shown`} />
            ) : (
              <img src={v.thumbUrl} alt={v.name.slice(PROOF_PREFIX.length)} loading="lazy" className="size-full object-cover" onError={() => setBroken((b) => new Set(b).add(v.id))} />
            )}
          </a>
        </li>
      ))}
    </ul>
  );
}
